import { useEffect, useMemo, useState } from 'react'
import { BulkPrintRow, prunedSelection, selectedRows } from '../utils/bulkPrint'
import {
  mergeSelection,
  toggleSelection,
  rangeBetween,
} from '../utils/shipmentSelection'

/**
 * Selección de guías para imprimir en el detalle de carga masiva.
 *
 * Vive en un hook y no dentro de la pantalla para poder testearlo: renderizar
 * `ShipmentBulkUploadDetail` entero exige Redux, router, el polling y cinco servicios,
 * así que un test de esa página rompería por motivos que no tienen nada que ver con la
 * selección. Acá se ejercita el mismo código que se envía, sin ese andamiaje.
 *
 * Trabaja con IDS (`shipment_bulk_upload_detail_id`) y no con objetos: el polling
 * reemplaza los objetos de `details` cada ~2 s, así que una selección de objetos
 * quedaría apuntando a instancias viejas.
 */
export interface UseBulkPrintSelectionArgs {
  /** Filas que SÍ se pueden imprimir (Executed con número de guía). */
  printableRows: BulkPrintRow[]
  /** Ids de las filas del filtro actual, en orden de tabla. */
  filteredIds: string[]
  /** Ids de la página visible, en orden. Es sobre esto que opera el rango con Shift. */
  paginatedIds: string[]
  /**
   * Cambia cuando cambia lo que se está viendo (búsqueda, página, tamaño de página).
   * Al cambiar se olvida el ancla del rango: sin esto, un Shift+click después de
   * filtrar calcularía el rango contra una lista que ya no existe y marcaría filas que
   * el operador nunca vio.
   */
  viewKey: string
}

export const useBulkPrintSelection = ({
  printableRows,
  filteredIds,
  paginatedIds,
  viewKey,
}: UseBulkPrintSelectionArgs) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null)

  /** Índice por id, para saber en O(1) si una fila lleva checkbox. */
  const printableById = useMemo(() => {
    const map = new Map<string, BulkPrintRow>()
    printableRows.forEach((r) => map.set(r.detailId, r))
    return map
  }, [printableRows])

  /**
   * Poda de ids muertos. El detalle se recarga solo y el operador puede borrar filas:
   * sin esto un id borrado quedaría en la selección para siempre y el contador diría
   * "3 guías seleccionadas" con solo 2 imprimibles.
   */
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = prunedSelection(prev, printableRows)
      return next.length === prev.length ? prev : next
    })
  }, [printableRows])

  useEffect(() => {
    setAnchorIndex(null)
  }, [viewKey])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  /** Filas seleccionadas, en orden de tabla y no de tildado. */
  const selectedPrintRows = useMemo(
    () => selectedRows(printableRows, selectedIds),
    [printableRows, selectedIds]
  )

  /** Filas imprimibles DEL FILTRO ACTUAL: es lo que abarca el checkbox de cabecera. */
  const filteredPrintableRows = useMemo(
    () =>
      filteredIds.flatMap((id) => {
        const row = printableById.get(id)
        return row ? [row] : []
      }),
    [filteredIds, printableById]
  )

  const allFilteredSelected =
    filteredPrintableRows.length > 0 &&
    filteredPrintableRows.every((r) => selectedSet.has(r.detailId))

  const isSelected = (detailId: string) => selectedSet.has(detailId)

  /**
   * Marca/desmarca una fila. Con Shift marca el tramo desde la última fila tocada.
   * `index` es el índice DENTRO DE LA PÁGINA VISIBLE.
   */
  const toggleRow = (detailId: string, index: number, shiftKey: boolean) => {
    if (shiftKey && anchorIndex !== null) {
      const range = rangeBetween(paginatedIds, anchorIndex, index).filter((id) =>
        printableById.has(id)
      )
      setSelectedIds((prev) => mergeSelection(prev, range, true, (id) => id))
      return
    }
    setAnchorIndex(index)
    setSelectedIds((prev) => toggleSelection(prev, detailId, (id) => id))
  }

  const toggleAllFiltered = () => {
    const ids = filteredPrintableRows.map((r) => r.detailId)
    setAnchorIndex(null)
    setSelectedIds((prev) =>
      mergeSelection(prev, ids, !allFilteredSelected, (id) => id)
    )
  }

  const clear = () => {
    setAnchorIndex(null)
    setSelectedIds([])
  }

  return {
    selectedIds,
    selectedPrintRows,
    printableById,
    filteredPrintableRows,
    allFilteredSelected,
    isSelected,
    toggleRow,
    toggleAllFiltered,
    clear,
  }
}
